/**
 * DocumentPreviewContextRenderer — fabryka `contextRenderers['document_preview_view']`
 * dla strony detalu rekordu (mirror `buildDocumentAdditionalRenderers` w
 * `apps/admin/src/pages/document-additional-renderers.tsx` — sam wzorzec fabryki,
 * inny placeholder-typ).
 *
 * Wpinają ją strony detalu kartotek (karty dokumentów, kontrahenta, pracownika, użytkownika,
 * zadania). Użycie na stronie hosta:
 *
 *   contextRenderers={{
 *     ...buildDocumentAdditionalRenderers(recordId, {...}),
 *     ...buildDocumentPreviewContextRenderer({
 *       ownerType: 'document', ownerId: recordId, companyId, extValues,
 *     }),
 *   }}
 *
 * `render` z `DocumentPreviewViewElement` (LayoutElementRegistry) obsługuje TYLKO
 * canvas buildera (`mode==='builder'`) — na realnej stronie rekordu ten typ jest
 * dispatchowany WYŁĄCZNIE przez ten plik (patrz `PLACEHOLDER_TYPES` w
 * `RecursiveFormRenderer.tsx`).
 *
 * `renderer` (wartość zwracana z fabryki) jest wołany jak ZWYKŁA FUNKCJA
 * (`contextRenderers[el.type]({element: el})` w `RecursiveFormRenderer`) — więc
 * żaden hook nie może być wywołany bezpośrednio w jej ciele. Dlatego zwraca JSX
 * montujący `DocumentPreviewInlineWidget` (prawdziwy komponent, hooki bezpieczne
 * wewnątrz jego cyklu życia) — ten sam wzorzec co `DocumentItemsColumnsCanvas` (osobny plik,
 * montowany przez `render` klocka `DocumentItemsViewElement`) / `AdditionalTableTab` w
 * `buildDocumentAdditionalRenderers`.
 */

import { type ReactNode, useState } from 'react';
import type { AttachmentOwnerType } from '@saas/shared';

import type { LayoutElement } from '@/components/form/RecursiveFormRenderer';

import { DocumentPreviewPanel } from './DocumentPreviewPanel';
import { readPreviewSourceSettings } from './preview-source';
import { useResolvedPreviewAttachments } from './use-resolved-preview-attachments';

export interface BuildDocumentPreviewContextRendererOptions {
  ownerType: AttachmentOwnerType;
  /** Id rekordu-właściciela. `null`/`undefined` w trybie create — widget wtedy
   *  renderuje pusty stan `DocumentPreviewPanel` (brak załączników do pokazania,
   *  zanim rekord zostanie zapisany po raz pierwszy). */
  ownerId: string | null | undefined;
  companyId?: string;
  /** Wartości pól dodatkowych BIEŻĄCEGO rekordu (slug → wartość) — jedyne źródło
   *  dla `source === 'field'` (patrz `preview-source.ts`). */
  extValues?: Record<string, unknown>;
}

interface DocumentPreviewInlineWidgetProps extends BuildDocumentPreviewContextRendererOptions {
  element: LayoutElement;
}

function DocumentPreviewInlineWidget({
  element,
  ownerType,
  ownerId,
  companyId,
  extValues,
}: DocumentPreviewInlineWidgetProps) {
  const { source, fieldSlug } = readPreviewSourceSettings(element.settings);
  const [activeId, setActiveId] = useState<string | null>(null);

  const { attachments } = useResolvedPreviewAttachments({
    ownerType,
    ownerId: ownerId ?? '',
    companyId,
    source,
    fieldSlug,
    extValues,
    enabled: !!ownerId,
  });

  const activeAttachmentId =
    activeId && attachments.some((a) => a.id === activeId)
      ? activeId
      : (attachments[0]?.id ?? null);

  return (
    <DocumentPreviewPanel
      attachments={attachments}
      activeAttachmentId={activeAttachmentId}
      onActiveAttachmentChange={setActiveId}
      mode="inline"
    />
  );
}

/** Buduje mapę contextRenderers z jednym wpisem: `document_preview_view`. */
export function buildDocumentPreviewContextRenderer(
  options: BuildDocumentPreviewContextRendererOptions,
): Record<string, (props: { element: LayoutElement }) => ReactNode> {
  return {
    document_preview_view: ({ element }) => (
      <DocumentPreviewInlineWidget element={element} {...options} />
    ),
  };
}
